interface CacheItem<T> {
  data: T;
  timestamp: number;
  version?: string;
}

export class CacheService {
  private static instances: Map<string, CacheService> = new Map();
  private static readonly DB_NAME = 'intenticoCache';
  private static readonly DB_VERSION = 1;
  private static readonly MAX_RETRIES = 3;
  private static readonly RETRY_DELAY = 1000; // 1 second
  private static readonly BLOCKED_TIMEOUT = 3000;
  private static dbConnection: IDBDatabase | null = null;
  private static deleteInProgress: Promise<void> | null = null;
  
  // Add cache version for invalidation
  private static readonly CACHE_VERSION = '1.0.1'; // Increment this to invalidate all caches
  
  private storeName: string;
  private defaultExpiration: number;

  private constructor(storeName: string, defaultExpiration: number = 20 * 60 * 1000) {
    this.storeName = storeName;
    this.defaultExpiration = defaultExpiration;
  }

  /**
   * Force cache invalidation for all users by checking version
   */
  public static async checkAndInvalidateCache(): Promise<void> {
    try {
      const currentVersion = localStorage.getItem('cacheVersion');
      if (currentVersion !== CacheService.CACHE_VERSION) {
        console.log('Cache version mismatch, clearing all caches');
        await CacheService.clearDatabaseData();
        localStorage.setItem('cacheVersion', CacheService.CACHE_VERSION);
      }
    } catch (error) {
      console.warn('Failed to check cache version:', error);
    }
  }

  /**
   * Clear data from all stores without deleting the database.
   * This avoids IndexedDB "delete blocked" issues from other tabs.
   */
  public static async clearDatabaseData(): Promise<void> {
    if (CacheService.deleteInProgress) {
      await CacheService.deleteInProgress;
    }

    CacheService.closeConnection();
    CacheService.instances.clear();

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(CacheService.DB_NAME, CacheService.DB_VERSION);

      request.onerror = () => {
        reject(request.error);
      };

      request.onupgradeneeded = () => {
        // Database did not exist yet; nothing to clear.
      };

      request.onsuccess = () => {
        const db = request.result;
        const storeNames = Array.from(db.objectStoreNames);

        if (storeNames.length === 0) {
          db.close();
          resolve();
          return;
        }

        const transaction = db.transaction(storeNames, 'readwrite');
        transaction.oncomplete = () => {
          db.close();
          resolve();
        };
        transaction.onerror = () => {
          db.close();
          reject(transaction.error);
        };
        transaction.onabort = () => {
          db.close();
          reject(transaction.error ?? new Error('Cache clear transaction aborted.'));
        };

        storeNames.forEach((storeName) => {
          transaction.objectStore(storeName).clear();
        });
      };
    });
  }

  /**
   * Delete the database to start fresh
   */
  public static async deleteDatabase(): Promise<void> {
    if (CacheService.deleteInProgress) {
      return CacheService.deleteInProgress;
    }

    CacheService.deleteInProgress = CacheService.deleteDatabaseWithRetry().finally(() => {
      CacheService.deleteInProgress = null;
    });

    return CacheService.deleteInProgress;
  }

  private static closeConnection(): void {
    if (CacheService.dbConnection) {
      CacheService.dbConnection.close();
      CacheService.dbConnection = null;
    }
  }

  private static async deleteDatabaseWithRetry(retryCount = 0): Promise<void> {
    CacheService.closeConnection();
    CacheService.instances.clear();

    return new Promise((resolve, reject) => {
      const request = indexedDB.deleteDatabase(CacheService.DB_NAME);
      let blocked = false;

      const blockedTimeout = window.setTimeout(() => {
        if (!blocked) {
          return;
        }

        if (retryCount < CacheService.MAX_RETRIES) {
          console.warn(`Database deletion blocked - retrying (${retryCount + 1}/${CacheService.MAX_RETRIES})`);
          CacheService.deleteDatabaseWithRetry(retryCount + 1)
            .then(resolve)
            .catch(reject);
          return;
        }

        reject(new Error('Database deletion remained blocked after retries.'));
      }, CacheService.BLOCKED_TIMEOUT);

      request.onerror = () => {
        window.clearTimeout(blockedTimeout);
        console.error('Failed to delete database:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        window.clearTimeout(blockedTimeout);
        console.log('Database deleted successfully');
        resolve();
      };

      request.onblocked = () => {
        blocked = true;
        console.warn('Database deletion blocked - waiting for connections to close');
      };
    });
  }

  /**
   * Get a CacheService instance for a specific store
   * @param storeName The name of the store to use (e.g., 'accounts', 'sales', 'users')
   * @param defaultExpiration Optional custom expiration time in milliseconds
   */
  public static getInstance(storeName: string, defaultExpiration?: number): CacheService {
    if (!CacheService.instances.has(storeName)) {
      CacheService.instances.set(storeName, new CacheService(storeName, defaultExpiration));
    } else if (defaultExpiration != null) {
      CacheService.instances.get(storeName)!.defaultExpiration = defaultExpiration;
    }
    return CacheService.instances.get(storeName)!;
  }

  private async initDB(retryCount = 0): Promise<IDBDatabase> {
    if (CacheService.deleteInProgress) {
      await CacheService.deleteInProgress;
    }

    if (CacheService.dbConnection) {
      return CacheService.dbConnection;
    }

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(CacheService.DB_NAME, CacheService.DB_VERSION);

      request.onerror = () => {
        if (retryCount < CacheService.MAX_RETRIES) {
          setTimeout(() => {
            this.initDB(retryCount + 1)
              .then(resolve)
              .catch(reject);
          }, CacheService.RETRY_DELAY);
        } else {
          reject(request.error);
        }
      };
      
      request.onsuccess = () => {
        CacheService.dbConnection = request.result;
        
        // Handle connection closure
        CacheService.dbConnection.onclose = () => {
          CacheService.dbConnection = null;
        };

        // Close stale connections immediately when a delete/upgrade starts.
        CacheService.dbConnection.onversionchange = () => {
          CacheService.closeConnection();
        };

        // Handle connection errors
        CacheService.dbConnection.onerror = (event) => {
          console.error('Database connection error:', event);
          CacheService.dbConnection = null;
        };
        
        resolve(CacheService.dbConnection);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName);
        }
      };
    });
  }

  /**
   * Get cached data for a specific key
   * @param key The cache key
   * @returns The cached data or null if not found/expired
   */
  public async get<T>(key: string): Promise<T | null> {
    try {
      const db = await this.initDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(this.storeName, 'readonly');
        const store = transaction.objectStore(this.storeName);
        const request = store.get(key);

        request.onerror = () => reject(request.error);
        
        request.onsuccess = () => {
          const result = request.result as CacheItem<T>;
          if (result && Date.now() - result.timestamp < this.defaultExpiration) {
            resolve(result.data);
          } else {
            // If expired, delete the item
            if (result) {
              this.delete(key).catch(console.warn);
            }
            resolve(null);
          }
        };
      });
    } catch (error) {
      console.warn('Cache read failed:', error);
      return null;
    }
  }

  /**
   * Store data in cache
   * @param key The cache key
   * @param data The data to cache
   */
  public async set<T>(key: string, data: T): Promise<void> {
    try {
      const db = await this.initDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(this.storeName, 'readwrite');
        const store = transaction.objectStore(this.storeName);
        const request = store.put({
          data,
          timestamp: Date.now()
        }, key);

        request.onerror = () => reject(request.error);
        
        request.onsuccess = () => resolve();
      });
    } catch (error) {
      console.warn('Failed to cache data:', error);
    }
  }

  /**
   * Clear all cached data in the current store
   */
  public async clear(): Promise<void> {
    try {
      const db = await this.initDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(this.storeName, 'readwrite');
        const store = transaction.objectStore(this.storeName);
        const request = store.clear();

        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve();
      });
    } catch (error) {
      console.warn('Failed to clear cache:', error);
    }
  }

  /**
   * Delete a specific cached item
   * @param key The cache key to delete
   */
  public async delete(key: string): Promise<void> {
    try {
      const db = await this.initDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(this.storeName, 'readwrite');
        const store = transaction.objectStore(this.storeName);
        const request = store.delete(key);

        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve();
      });
    } catch (error) {
      console.warn('Failed to delete cache item:', error);
    }
  }

  /**
   * Delete all cached items that start with the given prefix
   * Useful for clearing related cache items (e.g., all items for a specific client)
   * @param prefix The prefix to match against cache keys
   */
  public async deleteByPrefix(prefix: string): Promise<void> {
    try {
      const db = await this.initDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(this.storeName, 'readwrite');
        const store = transaction.objectStore(this.storeName);
        const request = store.openCursor();

        request.onerror = () => reject(request.error);
        request.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
          if (cursor) {
            if (cursor.key.toString().startsWith(prefix)) {
              cursor.delete();
            }
            cursor.continue();
          } else {
            resolve();
          }
        };
      });
    } catch (error) {
      console.warn('Failed to delete cache items by prefix:', error);
    }
  }
} 